<template>
	<div class="transactions-table" :class="[colourMode, { resizing: isResizing }]">
		<div class="table-header" :style="gridStyle">
			<div class="col-header col-date sortable" :class="{ sorted: !sortColumn || sortColumn === 'date' }" @click="toggleSort('date')">
				Date
				<span class="sort-icon" v-if="!sortColumn || sortColumn === 'date'">{{ sortColumn === 'date' && sortDirection === 'asc' ? '▲' : '▼' }}</span>
				<div class="resize-handle" @mousedown.stop="startResize($event, 0)"></div>
			</div>
			<div class="col-header col-merchant sortable" :class="{ sorted: sortColumn === 'merchant' }" @click="toggleSort('merchant')">
				Merchant
				<span class="sort-icon" v-if="sortColumn === 'merchant'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
				<div class="resize-handle" @mousedown.stop="startResize($event, 1)"></div>
			</div>
			<div class="col-header col-description sortable" :class="{ sorted: sortColumn === 'description' }" @click="toggleSort('description')">
				Description
				<span class="sort-icon" v-if="sortColumn === 'description'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
				<div class="resize-handle" @mousedown.stop="startResize($event, 2)"></div>
			</div>
			<div class="col-header col-category sortable" :class="{ sorted: sortColumn === 'category' }" @click="toggleSort('category')">
				Category
				<span class="sort-icon" v-if="sortColumn === 'category'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
				<div class="resize-handle" @mousedown.stop="startResize($event, 3)"></div>
			</div>
			<div class="col-header col-tags">
				Tags
				<div class="resize-handle" @mousedown.stop="startResize($event, 4)"></div>
			</div>
			<div class="col-header col-amount sortable" :class="{ sorted: sortColumn === 'amount' }" @click="toggleSort('amount')">
				Amount
				<span class="sort-icon" v-if="sortColumn === 'amount'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
			</div>
		</div>

		<!-- Grouped view (default/date sort) -->
		<template v-if="!sortColumn || sortColumn === 'date'">
			<div v-for="(group, gIndex) in sortedTransactions" :key="gIndex" class="transaction-group">
				<div class="date-header">{{ formatDateHeader(group[0]?.timestampString) }}</div>
				<template v-for="t in group" :key="t.id">
					<div
						class="transaction-row"
						:class="{ expanded: expandedTransactionId === t.id, highlighted: highlightedTransactionId === t.id || selectedTransactionId === t.id }"
						:style="gridStyle"
						@click="toggleExpand(t)"
					>
						<div class="col-date">{{ t.timestampString }}</div>
						<div class="col-merchant">{{ t.merchant?.name || t.merchantName }}</div>
						<div class="col-description">{{ t.description }}</div>
						<div class="col-category">
							<img
								v-if="getClassification(t)?.iconPath"
								:src="require('@/assets/transactions/' + getClassification(t).iconPath.toLowerCase().replace('.svg', '.png'))"
								class="category-icon"
							/>
							<img v-else :src="require('@/assets/round3.png')" class="category-icon" />
							<span>{{ getClassification(t)?.mgSubClassification || getClassification(t)?.mgClassification || 'Uncategorized' }}</span>
							<button class="tag-btn" @click.stop="onTagClick(t)" title="Change category">
								<v-icon size="14">mdi-pencil</v-icon>
							</button>
						</div>
						<div class="col-tags">
							<span class="tags-truncate">
								<span v-for="tag in t.customerTags" :key="tag.id" class="tag">#{{ tag.tag }}</span>
							</span>
							<span v-if="t.customerTags?.length" class="tags-tooltip">
								<span v-for="tag in t.customerTags" :key="tag.id" class="tag">#{{ tag.tag }}</span>
							</span>
						</div>
						<div class="col-amount" :class="{ positive: t.amount > 0 }">
							{{ t.amount < 0 ? formatter.format(t.amount * -1) : '+' + formatter.format(t.amount) }}
						</div>
					</div>
					<!-- Expanded Detail Panel -->
					<transition name="expand">
					<div v-if="expandedTransactionId === t.id" class="transaction-detail-panel">
						<div class="detail-grid">
							<div class="detail-item">
								<span class="detail-label">Date</span>
								<span class="detail-value">{{ formatFullDate(t.timestamp) }}</span>
							</div>
							<div class="detail-item" v-if="getTime(t.timestamp) !== '00:00:00'">
								<span class="detail-label">Time</span>
								<span class="detail-value">{{ getTime(t.timestamp) }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label">Transaction</span>
								<span class="detail-value">{{ t.type === 'DEBIT' ? 'Money Out' : 'Money In' }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label">Spending Analysis</span>
								<span class="detail-value">{{ t.excludeFromAnalysis ? 'Excluded' : 'Included' }}</span>
							</div>
							<div class="detail-item full-width">
								<span class="detail-label">Description</span>
								<span class="detail-value">{{ t.description }}</span>
							</div>
							<div class="detail-item full-width">
								<span class="detail-label">#Tags</span>
								<TagInput
									:selectableTags="availableTags"
									:modelValue="getSelectedTags(t)"
									:maxTags="5"
									:maxTagLength="20"
									@change="(tags) => handleTagsChange(t, tags)"
								/>
							</div>
						</div>
					</div>
					</transition>
				</template>
			</div>
		</template>

		<!-- Flat view (when sorting by non-date column) -->
		<template v-else>
			<template v-for="t in sortedTransactions" :key="t.id">
				<div
					class="transaction-row"
					:class="{ expanded: expandedTransactionId === t.id, highlighted: highlightedTransactionId === t.id || selectedTransactionId === t.id }"
					:style="gridStyle"
					@click="toggleExpand(t)"
				>
					<div class="col-date">{{ t.timestampString }}</div>
					<div class="col-merchant">{{ t.merchant?.name || t.merchantName }}</div>
					<div class="col-description">{{ t.description }}</div>
					<div class="col-category">
						<img
							v-if="getClassification(t)?.iconPath"
							:src="require('@/assets/transactions/' + getClassification(t).iconPath.toLowerCase().replace('.svg', '.png'))"
							class="category-icon"
						/>
						<img v-else :src="require('@/assets/round3.png')" class="category-icon" />
						<span>{{ getClassification(t)?.mgSubClassification || getClassification(t)?.mgClassification || 'Uncategorized' }}</span>
						<button class="tag-btn" @click.stop="onTagClick(t)" title="Change category">
							<v-icon size="14">mdi-pencil</v-icon>
						</button>
					</div>
					<div class="col-tags">
						<span class="tags-truncate">
							<span v-for="tag in t.customerTags" :key="tag.id" class="tag">#{{ tag.tag }}</span>
						</span>
						<span v-if="t.customerTags?.length" class="tags-tooltip">
							<span v-for="tag in t.customerTags" :key="tag.id" class="tag">#{{ tag.tag }}</span>
						</span>
					</div>
					<div class="col-amount" :class="{ positive: t.amount > 0 }">
						{{ t.amount < 0 ? formatter.format(t.amount * -1) : '+' + formatter.format(t.amount) }}
					</div>
				</div>
				<!-- Expanded Detail Panel -->
				<transition name="expand">
				<div v-if="expandedTransactionId === t.id" class="transaction-detail-panel">
					<div class="detail-grid">
						<div class="detail-item">
							<span class="detail-label">Date</span>
							<span class="detail-value">{{ formatFullDate(t.timestamp) }}</span>
						</div>
						<div class="detail-item" v-if="getTime(t.timestamp) !== '00:00:00'">
							<span class="detail-label">Time</span>
							<span class="detail-value">{{ getTime(t.timestamp) }}</span>
						</div>
						<div class="detail-item">
							<span class="detail-label">Transaction</span>
							<span class="detail-value">{{ t.type === 'DEBIT' ? 'Money Out' : 'Money In' }}</span>
						</div>
						<div class="detail-item">
							<span class="detail-label">Spending Analysis</span>
							<span class="detail-value">{{ t.excludeFromAnalysis ? 'Excluded' : 'Included' }}</span>
						</div>
						<div class="detail-item full-width">
							<span class="detail-label">Description</span>
							<span class="detail-value">{{ t.description }}</span>
						</div>
						<div class="detail-item full-width">
							<span class="detail-label">#Tags</span>
							<TagInput
								:selectableTags="availableTags"
								:modelValue="getSelectedTags(t)"
								:maxTags="5"
								:maxTagLength="20"
								@change="(tags) => handleTagsChange(t, tags)"
							/>
						</div>
					</div>
				</div>
				</transition>
			</template>
		</template>

		<!-- Empty State -->
		<div v-if="!transactions?.length" class="empty-state">
			<p>{{ emptyMessage }}</p>
		</div>
	</div>
</template>

<script>
import TagInput from '../../components/elements/TagInput.vue';

export default {
	name: "TransactionsTable",
	components: { TagInput },
	props: {
		transactions: {
			type: Array,
			default: () => []
		},
		colourMode: {
			type: String,
			default: 'light'
		},
		formatter: {
			type: Object,
			required: true
		},
		emptyMessage: {
			type: String,
			default: 'No transactions found'
		},
		highlightedTransactionId: {
			type: String,
			default: null
		},
		availableTags: {
			type: Array,
			default: () => []
		}
	},
	emits: ['row-click', 'tag-click', 'tags-change'],
	data() {
		return {
			columnWidths: [80, 220, null, 160, 120, 100], // null = flex
			isResizing: false,
			resizeIndex: null,
			startX: 0,
			startWidth: 0,
			justResized: false,
			sortColumn: null,
			sortDirection: 'desc',
			expandedTransactionId: null,
			selectedTransactionId: null
		}
	},
	computed: {
		gridStyle() {
			const cols = this.columnWidths.map(w => w ? `${w}px` : '1fr').join(' ');
			return { gridTemplateColumns: cols };
		},
		sortedTransactions() {
			if (!this.sortColumn || this.sortColumn === 'date') {
				// Return grouped, optionally reverse for date ascending
				if (this.sortColumn === 'date' && this.sortDirection === 'asc') {
					return [...this.transactions].reverse().map(g => [...g].reverse());
				}
				return this.transactions;
			}

			// Flatten groups for other sorts
			const flat = this.transactions.flatMap(group => group);

			flat.sort((a, b) => {
				let valA, valB;

				switch (this.sortColumn) {
					case 'merchant':
						valA = (a.merchant?.name || a.merchantName || '').toLowerCase();
						valB = (b.merchant?.name || b.merchantName || '').toLowerCase();
						break;
					case 'description':
						valA = (a.description || '').toLowerCase();
						valB = (b.description || '').toLowerCase();
						break;
					case 'category':
						valA = (this.getClassification(a)?.mgClassification || '').toLowerCase();
						valB = (this.getClassification(b)?.mgClassification || '').toLowerCase();
						break;
					case 'amount':
						valA = a.amount;
						valB = b.amount;
						break;
					default:
						return 0;
				}

				if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
				if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
				return 0;
			});

			return flat;
		}
	},
	methods: {
		getClassification(t) {
			return t.customerClassification || t.effectiveClassification || t.originalClassification?.mgClassification;
		},
		formatDateHeader(dateString) {
			const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
			const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
			if (dateString === today) return 'Today';
			if (dateString === yesterday) return 'Yesterday';
			return dateString;
		},
		startResize(e, index) {
			this.isResizing = true;
			this.resizeIndex = index;
			this.startX = e.clientX;
			this.startWidth = this.columnWidths[index] || 200;
			document.addEventListener('mousemove', this.onResize);
			document.addEventListener('mouseup', this.stopResize);
		},
		onResize(e) {
			if (!this.isResizing) return;
			const diff = e.clientX - this.startX;
			const newWidth = Math.max(50, this.startWidth + diff);
			this.columnWidths[this.resizeIndex] = newWidth;
		},
		stopResize() {
			this.isResizing = false;
			this.resizeIndex = null;
			this.justResized = true;
			document.removeEventListener('mousemove', this.onResize);
			document.removeEventListener('mouseup', this.stopResize);
			setTimeout(() => { this.justResized = false; }, 0);
		},
		toggleSort(column) {
			if (this.justResized) return;
			if (this.sortColumn === column) {
				this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
			} else {
				this.sortColumn = column;
				this.sortDirection = column === 'amount' ? 'desc' : 'asc';
			}
		},
		toggleExpand(transaction) {
			this.selectedTransactionId = transaction.id;
			if (this.expandedTransactionId === transaction.id) {
				this.expandedTransactionId = null;
			} else {
				this.expandedTransactionId = transaction.id;
				this.$emit('row-click', transaction);
			}
		},
		formatFullDate(timestamp) {
			if (!timestamp) return '';
			const date = new Date(timestamp);
			return date.toLocaleDateString('en-GB', {
				weekday: 'long',
				day: 'numeric',
				month: 'short',
				year: '2-digit'
			});
		},
		getTime(timestamp) {
			if (!timestamp) return '';
			return timestamp.split('T')[1]?.split('.')[0] || '';
		},
		getSelectedTags(transaction) {
			if (transaction.customerTags && Array.isArray(transaction.customerTags)) {
				return transaction.customerTags.map(t => t.tag);
			}
			return [];
		},
		handleTagsChange(transaction, tags) {
			this.$emit('tags-change', { transaction, tags });
		},
		onTagClick(transaction) {
			this.selectedTransactionId = transaction.id;
			this.$emit('tag-click', transaction);
		}
	},
	beforeUnmount() {
		document.removeEventListener('mousemove', this.onResize);
		document.removeEventListener('mouseup', this.stopResize);
	}
}
</script>

<style scoped>
.transactions-table {
	background: var(--light-bg-primary);
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
}

.table-header {
	display: grid;
	padding: 16px 20px;
	background: var(--light-bg-secondary);
	font-size: 12px;
	color: var(--light-text-secondary);
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.col-header {
	position: relative;
	padding-right: 8px;
}

.col-header.sortable {
	cursor: pointer;
	user-select: none;
}

.col-header.sortable:hover {
	color: var(--brand-purple);
}

.col-header.sorted {
	color: var(--brand-purple);
}

.sort-icon {
	font-size: 10px;
	margin-left: 4px;
}

.resize-handle {
	position: absolute;
	right: 0;
	top: 0;
	bottom: 0;
	width: 6px;
	cursor: col-resize;
	background: transparent;
}

.resize-handle:hover,
.transactions-table.resizing .resize-handle {
	background: rgba(65, 54, 85, 0.2);
}

.transactions-table.resizing {
	cursor: col-resize;
	user-select: none;
}

.date-header {
	padding: 12px 20px;
	background: var(--light-bg-tertiary);
	font-size: 13px;
	font-weight: bold;
	color: var(--brand-purple);
	border-top: 1px solid var(--light-border-subtle);
}

.transaction-group:first-child .date-header {
	border-top: none;
}

.transaction-row {
	display: grid;
	padding: 16px 20px;
	border-bottom: 1px solid var(--light-border);
	align-items: center;
	cursor: pointer;
	transition: background 0.2s;
}

.transaction-row:hover {
	background: var(--light-bg-tertiary);
}

.transaction-row:last-child {
	border-bottom: none;
}

.col-date {
	font-size: 13px;
	color: var(--light-text-secondary);
}

.col-merchant {
	font-size: 14px;
	color: var(--light-text-primary);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.col-category {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
}

.tag-btn {
	opacity: 0;
	background: none;
	border: none;
	padding: 4px;
	cursor: pointer;
	color: var(--light-text-secondary);
	border-radius: 4px;
	transition: opacity 0.15s, background 0.15s;
	margin-left: auto;
}

.transaction-row:hover .tag-btn {
	opacity: 1;
}

.tag-btn:hover {
	background: rgba(65, 54, 85, 0.1);
	color: var(--brand-purple);
}

.category-icon {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: var(--light-bg-selected);
	object-fit: cover;
}

.col-description {
	font-size: 13px;
	color: var(--light-text-secondary);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.col-tags {
	font-size: 12px;
	color: var(--background-color-alt2);
	position: relative;
}

.tags-truncate {
	display: block;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.tag {
	margin-right: 6px;
}

.tags-tooltip {
	position: absolute;
	bottom: 100%;
	left: 0;
	background: var(--brand-purple);
	color: white;
	padding: 10px 14px;
	border-radius: 8px;
	font-size: 12px;
	z-index: 100;
	box-shadow: 0 4px 12px rgba(65, 54, 85, 0.25);
	margin-bottom: 8px;
	opacity: 0;
	visibility: hidden;
	transform: translateY(4px);
	transition: all 0.2s ease;
	pointer-events: none;
	display: grid;
	grid-template-columns: repeat(2, auto);
	gap: 6px 16px;
	max-width: 300px;
}

.tags-tooltip .tag {
	color: white;
	white-space: nowrap;
}

.tags-tooltip::after {
	content: '';
	position: absolute;
	top: 100%;
	left: 16px;
	border: 6px solid transparent;
	border-top-color: var(--brand-purple);
}

.col-tags:hover .tags-tooltip {
	opacity: 1;
	visibility: visible;
	transform: translateY(0);
}

.col-amount {
	text-align: right;
	font-size: 14px;
	font-weight: bold;
}

.col-amount.positive {
	color: var(--positive-amount);
}

.empty-state {
	padding: 48px;
	text-align: center;
	color: var(--light-text-secondary);
}

/* Highlighted/Expanded Row */
.transaction-row.highlighted {
	background: var(--brand-purple-highlight);
	border-left: 3px solid var(--brand-purple);
}

.transaction-row.expanded {
	border-bottom: none;
}

/* Detail Panel */
.transaction-detail-panel {
	background: var(--light-bg-secondary);
	padding: 20px 24px 24px;
	border-bottom: 1px solid var(--light-border);
}

.detail-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 20px;
	max-width: 800px;
}

.detail-item {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.detail-item.full-width {
	grid-column: span 4;
}

.detail-label {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.detail-value {
	font-size: 14px;
	color: var(--light-text-primary);
}

.tags-value {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.detail-tag {
	color: var(--brand-purple);
}

/* TagInput in detail panel */
.detail-item :deep(.tag-input) {
	background: var(--light-bg-primary);
	border-color: var(--light-border-strong);
	min-height: 44px;
}

.detail-item :deep(.tag-menu) {
	z-index: 100;
}

/* Expand Animation */
.expand-enter-active,
.expand-leave-active {
	transition: all 0.25s ease-out;
	overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
	opacity: 0;
	max-height: 0;
	padding-top: 0;
	padding-bottom: 0;
}

.expand-enter-to,
.expand-leave-from {
	opacity: 1;
	max-height: 300px;
}

/* Dark Mode */
.transactions-table.dark {
	background: var(--dark-bg-secondary);
}

.transactions-table.dark .table-header {
	background: var(--dark-bg-primary);
	color: var(--dark-text-secondary);
}

.transactions-table.dark .col-header.sortable:hover,
.transactions-table.dark .col-header.sorted {
	color: var(--dark-text-primary);
}

.transactions-table.dark .date-header {
	background: var(--dark-bg-primary);
	color: var(--dark-text-primary);
	border-color: var(--dark-border);
}

.transactions-table.dark .transaction-row {
	border-color: var(--dark-border);
}

.transactions-table.dark .transaction-row:hover {
	background: var(--dark-bg-hover);
}

.transactions-table.dark .col-merchant {
	color: var(--dark-text-primary);
}

.transactions-table.dark .col-date,
.transactions-table.dark .col-description,
.transactions-table.dark .col-category {
	color: var(--dark-text-secondary);
}

.transactions-table.dark .tags-tooltip {
	background: var(--brand-purple-light);
	color: white;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.transactions-table.dark .tags-tooltip::after {
	border-top-color: var(--brand-purple-light);
}

.transactions-table.dark .empty-state {
	color: var(--dark-text-secondary);
}

.transactions-table.dark .tag-btn {
	color: var(--dark-text-secondary);
}

.transactions-table.dark .tag-btn:hover {
	background: rgba(255, 255, 255, 0.1);
	color: var(--dark-text-primary);
}

.transactions-table.dark .transaction-row.highlighted {
	background: rgba(65, 54, 85, 0.3);
	border-left-color: var(--category-everyday);
}

.transactions-table.dark .transaction-detail-panel {
	background: var(--dark-bg-primary);
	border-color: var(--dark-border);
}

.transactions-table.dark .detail-label {
	color: var(--dark-text-secondary);
}

.transactions-table.dark .detail-value {
	color: var(--dark-text-primary);
}

.transactions-table.dark .detail-tag {
	color: var(--category-income);
}

.transactions-table.dark .detail-item :deep(.tag-input) {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
}

.transactions-table.dark .detail-item :deep(.tag-input input) {
	color: var(--dark-text-primary) !important;
}

.transactions-table.dark .detail-item :deep(.tag-menu) {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
}

.transactions-table.dark .detail-item :deep(.tag-menu-item:hover) {
	background: var(--dark-bg-hover);
}
</style>
